import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Empty, Tag, Tooltip, Checkbox, Input } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faSave, faX, faXmark } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateFormAction,
  updateLeadingZeroe,
} from "../../../redux/LeadingZeroes/leadingZeroesSlice";
import {
  useLazyGetLeadingZeroesQuery,
  usePostLeadingZeroesMutation,
  usePutLeadingZeroesMutation,
} from "../../../redux/LeadingZeroes/leadingZeroesAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import LeadingZeroesTableActions from "./LeadingZeroesTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { FormAction } from "../../../types/enums";
import { LeadingZeroeInterface } from "../../../interfaces/LeadingZeroes.interface";

function LeadingZeroesTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { Column, ColumnGroup } = Table;
  const BLANK_LEADING_ZEROE: LeadingZeroeInterface = {
    id: "",
    name: "",
    fromDeviceSerial: false,
    refreshAll: false,
    reinitializeAllDevices: false,
    rvu: false,
    setOneBit: false,
    setOneByte: false,
  };

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [data, setData] = useState<LeadingZeroeInterface[]>([]);
  const [leadingZeroe, setLeadingZeroe] = useState<LeadingZeroeInterface>(BLANK_LEADING_ZEROE);
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [totalRows, setTotalRows] = useState<number>(0);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetLeadingZeroes,
    {
      data: dataGetLeadingZeroes,
      isLoading: isLoadingGetLeadingZeroes,
      isFetching: isFetchingGetLeadingZeroes,
      isError: isErrorGetLeadingZeroes,
      error: errorGetLeadingZeroes,
    },
  ] = useLazyGetLeadingZeroesQuery();

  const [
    triggerPostLeadingZeroe,
    {
      isSuccess: isSuccessPostLeadingZeroe,
      isLoading: isLoadingPostLeadingZeroe,
      isError: isErrorPostLeadingZeroe,
      error: errorPostLeadingZeroe,
      reset: resetPostLeadingZeroe,
    },
  ] = usePostLeadingZeroesMutation();

  const [
    triggerPutLeadingZeroe,
    {
      isSuccess: isSuccessPutLeadingZeroe,
      isLoading: isLoadingPutLeadingZeroe,
      isError: isErrorPutLeadingZeroe,
      error: errorPutLeadingZeroe,
      reset: resetPutLeadingZeroe,
    },
  ] = usePutLeadingZeroesMutation();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const {
    refreshTable,
    formAction,
    leadingZeroe: selectedLeadingZeroe,
  } = useSelector((state: RootState) => state.leadingZeroe);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getInitialData = async () => {
    const token = await GETJwtToken();
    triggerGetLeadingZeroes({
      token,
    });
  };

  const onClickRefreshTable = async () => {
    getInitialData();
  };

  const onChangeCheckBox = (newValue: boolean, name: string) => {
    const copy: LeadingZeroeInterface = structuredClone(leadingZeroe);
    copy[name] = newValue;
    setLeadingZeroe(copy);
  };

  const onChangeFormValueName = (newValue: string) => {
    const copy: LeadingZeroeInterface = structuredClone(leadingZeroe);
    copy.name = newValue;
    setLeadingZeroe(copy);
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    if (formAction === FormAction.Create) {
      const BODY = {
        token,
        body: leadingZeroe,
      };
      triggerPostLeadingZeroe(BODY);
    } else {
      const BODY = {
        token,
        id: leadingZeroe.id,
        body: leadingZeroe,
      };
      triggerPutLeadingZeroe(BODY);
    }
  };

  const resetForm = () => {
    resetPostLeadingZeroe();
    resetPutLeadingZeroe();
    setLeadingZeroe(BLANK_LEADING_ZEROE);
    setErrorMessage({});
    dispatch(updateFormAction(FormAction.Create));
    dispatch(updateLeadingZeroe(BLANK_LEADING_ZEROE));
  };

  const checkFormIsValid = () => {
    let formIsValid = true;
    if (leadingZeroe.name === "") {
      formIsValid = false;
    }
    return formIsValid;
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (refreshTable) {
      onClickRefreshTable();
    }
  }, [refreshTable]);

  useEffect(() => {
    if (dataGetLeadingZeroes && dataGetLeadingZeroes.data) {
      setData(dataGetLeadingZeroes.data);
      setTotalRows(dataGetLeadingZeroes.total);
    }
  }, [dataGetLeadingZeroes]);

  useEffect(() => {
    if (errorGetLeadingZeroes) {
      setErrorMessage(errorGetLeadingZeroes);
      setData([]);
    }
  }, [errorGetLeadingZeroes]);

  useEffect(() => {
    resetForm();
  }, [
    isSuccessPostLeadingZeroe,
    isSuccessPutLeadingZeroe,
    isErrorPostLeadingZeroe,
    isErrorPutLeadingZeroe,
  ]);

  useEffect(() => {
    if (formAction === FormAction.Create) {
      setLeadingZeroe(BLANK_LEADING_ZEROE);
    }
  }, [formAction]);

  useEffect(() => {
    setLeadingZeroe(selectedLeadingZeroe);
  }, [selectedLeadingZeroe]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div>
        {hasPermission(Permission.LeadingZeroesView, permissionSet ?? []) ? (
          <Table
            locale={{
              emptyText: (
                <TableNoDataBox
                  isError={isErrorGetLeadingZeroes}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="No data found"
                />
              ),
            }}
            rowClassName={(record, index) => getTableRowClass(index, theme)}
            dataSource={data}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
            size="small"
            className="mt-3"
            loading={
              isFetchingGetLeadingZeroes ||
              isLoadingGetLeadingZeroes ||
              isLoadingPostLeadingZeroe ||
              isLoadingPutLeadingZeroe
            }
            footer={() => (
              <div className="generalStyles__flexEnd">
                <Tag>{totalRows} Rows</Tag>
              </div>
            )}
          >
            {/** NAME */}
            <ColumnGroup title="Name">
              <Column
                width={200}
                title={
                  <Input
                    size="small"
                    placeholder="Name"
                    value={formAction === FormAction.Create ? leadingZeroe.name : ""}
                    disabled={formAction === FormAction.Edit}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      onChangeFormValueName(event.currentTarget.value);
                    }}
                  />
                }
                dataIndex="name"
                key="name"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Input
                        size="small"
                        placeholder="Name"
                        value={leadingZeroe.name}
                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                          onChangeFormValueName(event.currentTarget.value);
                        }}
                      />
                    ) : (
                      <span>{text}</span>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** FROM DEVICE SERIAL */}
            <ColumnGroup title="From Device Serial">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={
                      formAction === FormAction.Create ? leadingZeroe.fromDeviceSerial : false
                    }
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "fromDeviceSerial");
                    }}
                  />
                }
                dataIndex="fromDeviceSerial"
                key="fromDeviceSerial"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.fromDeviceSerial}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "fromDeviceSerial");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** REFRESH ALL */}
            <ColumnGroup title="Refresh All">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={formAction === FormAction.Create ? leadingZeroe.refreshAll : false}
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "refreshAll");
                    }}
                  />
                }
                dataIndex="refreshAll"
                key="refreshAll"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.refreshAll}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "refreshAll");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** REINITIALIZE ALL DEVICES */}
            <ColumnGroup title="Reinitialize All Devices">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={
                      formAction === FormAction.Create ? leadingZeroe.reinitializeAllDevices : false
                    }
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "reinitializeAllDevices");
                    }}
                  />
                }
                dataIndex="reinitializeAllDevices"
                key="reinitializeAllDevices"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.reinitializeAllDevices}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "reinitializeAllDevices");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** RVU */}
            <ColumnGroup title="RVU">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={formAction === FormAction.Create ? leadingZeroe.rvu : false}
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "rvu");
                    }}
                  />
                }
                dataIndex="rvu"
                key="rvu"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.rvu}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "rvu");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** SET ONE BIT */}
            <ColumnGroup title="Set One Bit">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={formAction === FormAction.Create ? leadingZeroe.setOneBit : false}
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "setOneBit");
                    }}
                  />
                }
                dataIndex="setOneBit"
                key="setOneBit"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.setOneBit}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "setOneBit");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** SET ONE BYTE */}
            <ColumnGroup title="Set One Byte">
              <Column
                width={200}
                title={
                  <Checkbox
                    disabled={formAction === FormAction.Edit}
                    checked={formAction === FormAction.Create ? leadingZeroe.setOneByte : false}
                    onChange={(e) => {
                      onChangeCheckBox(e.target.checked, "setOneByte");
                    }}
                  />
                }
                dataIndex="setOneByte"
                key="setOneByte"
                render={(text, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <Checkbox
                        disabled={formAction === FormAction.Edit}
                        checked={leadingZeroe.setOneByte}
                        onChange={(e) => {
                          onChangeCheckBox(e.target.checked, "setOneByte");
                        }}
                      />
                    ) : (
                      <>
                        {text ? (
                          <FontAwesomeIcon
                            className="generalStyles__success"
                            icon={faCheckCircle}
                          />
                        ) : (
                          <FontAwesomeIcon className="generalStyles__error" icon={faXmark} />
                        )}
                      </>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** ACTIONS */}
            <ColumnGroup align="center" title={t("edgeManagement.banks.actions")} width={200}>
              <Column
                align="center"
                width={200}
                title={
                  <div className="generalStyles__flexCenter">
                    <div>
                      <Tooltip title={t("general.save")}>
                        <Button
                          className={
                            formAction === FormAction.Create && checkFormIsValid()
                              ? "buttonStyle__10"
                              : ""
                          }
                          disabled={!checkFormIsValid() || formAction === FormAction.Edit}
                          onClick={onClickSave}
                          icon={<FontAwesomeIcon icon={faSave} />}
                        />
                      </Tooltip>
                    </div>
                    <div className="generalStyles__mlFix">
                      <Tooltip title={t("general.clear")}>
                        <Button
                          className={formAction === FormAction.Create ? "buttonStyle__14" : ""}
                          onClick={resetForm}
                          disabled={formAction === FormAction.Edit}
                          icon={<FontAwesomeIcon icon={faX} />}
                        />
                      </Tooltip>
                    </div>
                  </div>
                }
                key="action"
                render={(_: any, record: LeadingZeroeInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === leadingZeroe.id ? (
                      <div className="generalStyles__flexCenter">
                        <div>
                          <Tooltip title={t("general.save")}>
                            <Button
                              className="buttonStyle__10"
                              disabled={!checkFormIsValid()}
                              onClick={onClickSave}
                              icon={<FontAwesomeIcon icon={faSave} />}
                            />
                          </Tooltip>
                        </div>
                        <div className="generalStyles__mlFix">
                          <Tooltip title={t("general.cancel")}>
                            <Button
                              className="buttonStyle__14"
                              onClick={resetForm}
                              icon={<FontAwesomeIcon icon={faX} />}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <LeadingZeroesTableActions selectedItem={record} />
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
          </Table>
        ) : (
          <>
            <div className="generalStyles__noAccessToListTable">
              <Empty
                description={t("organizationManagement.listNotAvailableOrganizations")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          </>
        )}
      </div>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostLeadingZeroe}
        isSuccess={isSuccessPostLeadingZeroe}
        requestType="POST"
        error={errorPostLeadingZeroe}
        name="Leading Zeroe"
      />
      <GlobalAlert2
        isError={isErrorPutLeadingZeroe}
        isSuccess={isSuccessPutLeadingZeroe}
        requestType="PUT"
        error={errorPutLeadingZeroe}
        name="Leading Zeroe"
      />
    </div>
  );
}

export default LeadingZeroesTable;
